/**
 * A minimal, dependency-free ZIP writer — enough to hand the student a folder.
 *
 * The Starter App Template stopped being one file (see projectTemplate.js), and a
 * browser can only hand over several files as an archive. This builds one.
 *
 * WHY NOT A LIBRARY
 * Everything in the template is small text — eleven files, well under 40 KB in
 * total — so compression buys nothing a beginner would notice, and "stored" (no
 * compression) is the one ZIP mode that needs no deflate implementation at all.
 * That makes the whole writer ~60 lines of header packing, which is cheaper than
 * adding a dependency to a bundle that every student on a phone downloads.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *   - No compression (method 0). See above.
 *   - No ZIP64. The format caps a stored entry at 4 GB; our largest is a few KB.
 *   - No explicit directory entries. Paths carry their folders ("my-app/app.js"),
 *     which every extractor since the 1990s creates implicitly — Windows Explorer,
 *     macOS Archive Utility, `unzip`, and Node's own tooling all agree here.
 *
 * The one thing that IS load-bearing: every path must sit under a single root
 * folder. macOS expands a zip in place, so an archive of eleven loose files
 * scatters them across the student's Downloads folder instead of producing the
 * project folder Module 6 then asks them to open. `buildProjectZip` enforces it.
 */

const ACCENT_MARKS = new RegExp("[\u0300-\u036f]", "g");

/** Standard CRC-32 (IEEE 802.3) table, built once. */
const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let i = 0; i < 256; i += 1) {
        let c = i;
        for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        table[i] = c >>> 0;
    }
    return table;
})();

export const crc32 = (bytes) => {
    let c = 0xffffffff;
    for (let i = 0; i < bytes.length; i += 1) {
        c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    }
    return (c ^ 0xffffffff) >>> 0;
};

/**
 * MS-DOS packed date and time, which is all the ZIP local header has room for.
 *
 * Two-second resolution and a 1980 epoch are the format's, not ours. Anything
 * before 1980 cannot be represented, so a machine with a badly wrong clock is
 * clamped rather than allowed to write a negative year into the header and
 * produce an archive some extractors reject.
 */
const dosDateTime = (date) => {
    const year = Math.max(1980, date.getFullYear());
    return {
        time: (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1),
        date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
    };
};

/** Little-endian writer over a fixed-size buffer — the only shape ZIP headers need. */
const writer = (size) => {
    const bytes = new Uint8Array(size);
    const view = new DataView(bytes.buffer);
    let at = 0;
    return {
        bytes,
        u16(v) { view.setUint16(at, v, true); at += 2; },
        u32(v) { view.setUint32(at, v >>> 0, true); at += 4; },
        raw(src) { bytes.set(src, at); at += src.length; },
    };
};

const utf8 = (text) => new TextEncoder().encode(text);

/**
 * Pack a set of text files into a stored ZIP archive.
 *
 * @param {Array<{name: string, text: string}>} files  Paths use "/" separators.
 * @param {Date} [now]  Modification stamp for every entry; injectable so tests
 *                      can produce a byte-identical archive twice.
 * @returns {Uint8Array} The complete archive.
 */
export const zipSync = (files, now = new Date()) => {
    const stamp = dosDateTime(now);

    // Pass 1: encode everything and record where each local header lands, because
    // the central directory has to point back at those offsets.
    let offset = 0;
    const entries = files.map(({ name, text }) => {
        const nameBytes = utf8(name);
        const body = utf8(text == null ? "" : String(text));
        const entry = { nameBytes, body, crc: crc32(body), offset };
        // 30 = fixed part of a local file header.
        offset += 30 + nameBytes.length + body.length;
        return entry;
    });

    const localSize = offset;
    // 46 = fixed part of a central directory header.
    const centralSize = entries.reduce((sum, e) => sum + 46 + e.nameBytes.length, 0);
    const out = writer(localSize + centralSize + 22);

    // Bit 11 declares that names are UTF-8. Ours are ASCII today, but the flag is
    // what keeps a future accented path from being read as the extractor's local
    // code page.
    const FLAGS = 0x0800;

    for (const e of entries) {
        out.u32(0x04034b50);        // local file header signature
        out.u16(20);                // version needed to extract (2.0)
        out.u16(FLAGS);
        out.u16(0);                 // method: stored
        out.u16(stamp.time);
        out.u16(stamp.date);
        out.u32(e.crc);
        out.u32(e.body.length);     // compressed size == uncompressed, stored
        out.u32(e.body.length);
        out.u16(e.nameBytes.length);
        out.u16(0);                 // extra field length
        out.raw(e.nameBytes);
        out.raw(e.body);
    }

    for (const e of entries) {
        out.u32(0x02014b50);        // central directory header signature
        out.u16(20);                // version made by
        out.u16(20);                // version needed to extract
        out.u16(FLAGS);
        out.u16(0);                 // method: stored
        out.u16(stamp.time);
        out.u16(stamp.date);
        out.u32(e.crc);
        out.u32(e.body.length);
        out.u32(e.body.length);
        out.u16(e.nameBytes.length);
        out.u16(0);                 // extra field length
        out.u16(0);                 // file comment length
        out.u16(0);                 // disk number start
        out.u16(0);                 // internal attributes
        out.u32(0);                 // external attributes
        out.u32(e.offset);
        out.raw(e.nameBytes);
    }

    out.u32(0x06054b50);            // end of central directory signature
    out.u16(0);                     // this disk
    out.u16(0);                     // disk with the central directory
    out.u16(entries.length);        // entries on this disk
    out.u16(entries.length);        // entries total
    out.u32(centralSize);
    out.u32(localSize);             // offset of the central directory
    out.u16(0);                     // archive comment length

    return out.bytes;
};

/**
 * Turn an app name into something safe to use as a folder and file name.
 *
 * Students name their apps "Rosa's Tacos" and "Cristian Real State". Both become
 * folder names on their desktop, get typed at a terminal in Module 6, and end up
 * in a URL if they deploy — so this is the same lower-case-and-dashes shape the
 * V2611 checklist already tells them to use by hand.
 */
export const slugify = (name, fallback = "my-app") => {
    const slug = String(name || "")
        .normalize("NFD")
        .replace(ACCENT_MARKS, "") // strip the accents NFD just split off
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 40)
        .replace(/-+$/g, "");              // slice may have left a trailing dash
    return slug || fallback;
};

/**
 * Pack a `{ path: contents }` map into an archive rooted at one folder.
 *
 * @param {Object<string, string>} fileMap  Paths relative to the project root.
 * @param {string} rootFolder  Already slugified; every path is nested under it.
 * @param {Date} [now]
 * @returns {Uint8Array}
 */
export const buildProjectZip = (fileMap, rootFolder, now) =>
    zipSync(
        Object.entries(fileMap).map(([name, text]) => ({ name: `${rootFolder}/${name}`, text })),
        now
    );

export default zipSync;
